import { PayloadAction } from "@reduxjs/toolkit";
import { AlertHelper } from "helpers";
import { EAlertType } from "helpers/alertHelper";
import {
  ICreatePalletsRequest,
  ICreateWarehouseRequest,
  IGetByIdRequest,
  IPallet,
  IUpdateWarehouseRequest,
  IWarehouse,
} from "model";
import { IApiResponse } from "model/Common/IApiResponse";
import { IErrorResponse } from "model/Common/IErrorResponse";
import { IPaginateApiResponse } from "model/Common/IPaginateApiResponse";
import { IPaginationRequest } from "model/Common/IPaginationRequest";
import {
  ICompany,
  ICreateCompanyRequest,
  IUpdateCompanyRequest,
} from "model/Company/ICompany";
import {
  ICreateLocationRequest,
  ILocation,
  IUpdateLocationRequest,
} from "model/MasterData/ILocation";
import {
  ICreateProductRequest,
  IProduct,
  IUpdateProductRequest,
} from "model/Product/IProduct";
import { IProductCategory } from "model/MasterData/IProductCategory";
import { IUnitMeasure } from "model/MasterData/IUnitMeasure";
import {
  ICreateUserRequest,
  IUpdateUserRequest,
  IUser,
} from "model/User/IUser";
import {
  ICreateZoneRequest,
  IUpdateZoneRequest,
  IZone,
} from "model/Zone/IZone";
import { IZoneTemperature } from "model/MasterData/IZoneTemperature";
import { put, takeEvery, call } from "redux-saga/effects";
import { MasterDataActions } from "redux/actions";
import { MasterDataApiService } from "services";

/**
 * @Warehouse
 */
function* getAllWarehouses(action: PayloadAction<IPaginationRequest>) {
  try {
    const result: IPaginateApiResponse<IWarehouse[]> = yield call(
      MasterDataApiService.getAllWarehouses,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getAllWarehouses.success(result));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getAllWarehouses.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getAllWarehouses.failed(error));
  }
}

function* getWarehouseById(action: PayloadAction<IGetByIdRequest>) {
  try {
    const result: IApiResponse<IWarehouse> = yield call(
      MasterDataApiService.getWarehouseById,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getWarehouseById.success(result.data!));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getWarehouseById.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getWarehouseById.failed(error));
  }
}

function* createWarehouse(action: PayloadAction<ICreateWarehouseRequest>) {
  try {
    const result: IApiResponse<IWarehouse> = yield call(
      MasterDataApiService.createWarehouse,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.createWarehouse.success(result.data!));
      AlertHelper.showAlert("Tạo kho thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.createWarehouse.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.createWarehouse.failed(error));
  }
}

function* updateWarehouse(action: PayloadAction<IUpdateWarehouseRequest>) {
  try {
    const result: IApiResponse<IWarehouse> = yield call(
      MasterDataApiService.updateWarehouse,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.updateWarehouse.success(result.data!));
      AlertHelper.showAlert("Cập nhật kho thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.updateWarehouse.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.updateWarehouse.failed(error));
  }
}

function* deleteWarehouse(action: PayloadAction<{ id: string }>) {
  try {
    const result: IApiResponse<any> = yield call(
      MasterDataApiService.deleteWarehouse,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.deleteWarehouse.success(result));
      AlertHelper.showAlert("Xoá kho thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.deleteWarehouse.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.deleteWarehouse.failed(error));
  }
}

/**
 * @Product
 */
function* getAllProducts(action: PayloadAction<IPaginationRequest>) {
  try {
    const result: IPaginateApiResponse<IProduct[]> = yield call(
      MasterDataApiService.getAllProducts,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getAllProducts.success(result));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getAllProducts.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getAllProducts.failed(error));
  }
}
function* getProductById(action: PayloadAction<IGetByIdRequest>) {
  try {
    const result: IApiResponse<IProduct> = yield call(
      MasterDataApiService.getProductById,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getProductById.success(result.data!));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getProductById.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getProductById.failed(error));
  }
}

function* createProduct(action: PayloadAction<ICreateProductRequest>) {
  try {
    const result: IApiResponse<IProduct> = yield call(
      MasterDataApiService.createProduct,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.createProduct.success(result.data!));
      AlertHelper.showAlert("Tạo sản phẩm thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.createProduct.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.createProduct.failed(error));
  }
}

function* updateProduct(action: PayloadAction<IUpdateProductRequest>) {
  try {
    const result: IApiResponse<IProduct> = yield call(
      MasterDataApiService.updateProduct,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.updateProduct.success(result.data!));
      AlertHelper.showAlert("Cập nhật sản phẩm thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.updateProduct.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.updateProduct.failed(error));
  }
}

function* deleteProduct(action: PayloadAction<{ id: string }>) {
  try {
    const result: IApiResponse<any> = yield call(
      MasterDataApiService.deleteProduct,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.deleteProduct.success(result));
      AlertHelper.showAlert("Xoá sản phẩm thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.deleteProduct.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.deleteProduct.failed(error));
  }
}

/**
 * @Pallet
 */
function* getAllPallets(action: PayloadAction<IPaginationRequest>) {
  try {
    const result: IPaginateApiResponse<IPallet[]> = yield call(
      MasterDataApiService.getAllPallets,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getAllPallets.success(result));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getAllPallets.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getAllPallets.failed(error));
  }
}

function* createPallets(action: PayloadAction<ICreatePalletsRequest>) {
  try {
    const result: IApiResponse<IPallet> = yield call(
      MasterDataApiService.createPallet,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.createPallets.success(result.data!));
      AlertHelper.showAlert("Tạo Pallet thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.createPallets.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.createPallets.failed(error));
  }
}

/**
 * @Location
 */
function* getAllLocations(action: PayloadAction<IPaginationRequest>) {
  try {
    const result: IPaginateApiResponse<any> = yield call(
      MasterDataApiService.getAllLocations,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getAllLocations.success(result));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getAllLocations.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getAllLocations.failed(error));
  }
}
function* createLocation(action: PayloadAction<ICreateLocationRequest>) {
  try {
    const result: IApiResponse<ILocation> = yield call(
      MasterDataApiService.createLocation,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.createLocation.success(result.data!));
      AlertHelper.showAlert("Tạo vị trí thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.createLocation.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.createLocation.failed(error));
  }
}

function* updateLocation(action: PayloadAction<IUpdateLocationRequest>) {
  try {
    const result: IApiResponse<ILocation> = yield call(
      MasterDataApiService.updateLocation,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.updateLocation.success(result.data!));
      AlertHelper.showAlert("Cập nhật vị trí thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.updateLocation.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.updateLocation.failed(error));
  }
}

function* deleteLocation(action: PayloadAction<{ id: string }>) {
  try {
    const result: IApiResponse<any> = yield call(
      MasterDataApiService.deleteLocation,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.deleteLocation.success(result));
      AlertHelper.showAlert("Xoá vị trí thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.deleteLocation.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.deleteLocation.failed(error));
  }
}

/**
 * @Zone
 */
function* getAllZones(action: PayloadAction<IPaginationRequest>) {
  try {
    const result: IPaginateApiResponse<IZone[]> = yield call(
      MasterDataApiService.getAllZones,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getAllZones.success(result));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getAllZones.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getAllZones.failed(error));
  }
}
function* createZone(action: PayloadAction<ICreateZoneRequest>) {
  try {
    const result: IApiResponse<IZone> = yield call(
      MasterDataApiService.createZone,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.createZone.success(result.data!));
      AlertHelper.showAlert("Tạo khu vực thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.createZone.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.createZone.failed(error));
  }
}

function* updateZone(action: PayloadAction<IUpdateZoneRequest>) {
  try {
    const result: IApiResponse<IZone> = yield call(
      MasterDataApiService.updateZone,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.updateZone.success(result.data!));
      AlertHelper.showAlert("Cập nhật khu vực thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.updateZone.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.updateZone.failed(error));
  }
}

function* deleteZone(action: PayloadAction<{ id: string }>) {
  try {
    const result: IApiResponse<any> = yield call(
      MasterDataApiService.deleteZone,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.deleteZone.success(result));
      AlertHelper.showAlert("Xoá khu vực thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.deleteZone.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.deleteZone.failed(error));
  }
}

/**
 * @Company
 */
function* getAllCompanies(action: PayloadAction<IPaginationRequest>) {
  try {
    const result: IPaginateApiResponse<ICompany[]> = yield call(
      MasterDataApiService.getAllCompanies,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getAllCompanies.success(result));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getAllCompanies.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getAllCompanies.failed(error));
  }
}
function* getCompanyById(action: PayloadAction<IGetByIdRequest>) {
  try {
    const result: IApiResponse<ICompany> = yield call(
      MasterDataApiService.getCompanyById,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getCompanyById.success(result.data!));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getCompanyById.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getCompanyById.failed(error));
  }
}
function* createCompany(action: PayloadAction<ICreateCompanyRequest>) {
  try {
    const result: IApiResponse<ICompany> = yield call(
      MasterDataApiService.createCompany,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.createCompany.success(result.data!));
      AlertHelper.showAlert("Tạo khách hàng thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.createCompany.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.createCompany.failed(error));
  }
}

function* updateCompany(action: PayloadAction<IUpdateCompanyRequest>) {
  try {
    const result: IApiResponse<ICompany> = yield call(
      MasterDataApiService.updateCompany,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.updateCompany.success(result.data!));
      AlertHelper.showAlert(
        "Cập nhật khách hàng thành công",
        EAlertType.SUCCESS
      );
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.updateCompany.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.updateCompany.failed(error));
  }
}

function* deleteCompany(action: PayloadAction<{ id: string }>) {
  try {
    const result: IApiResponse<any> = yield call(
      MasterDataApiService.deleteCompany,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.deleteCompany.success(result));
      AlertHelper.showAlert("Xoá khách hàng thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.deleteCompany.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.deleteCompany.failed(error));
  }
}

/**
 * @User
 */
function* getAllUsers(action: PayloadAction<IPaginationRequest>) {
  try {
    const result: IPaginateApiResponse<IUser[]> = yield call(
      MasterDataApiService.getAllUsers,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getAllUsers.success(result));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getAllUsers.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getAllUsers.failed(error));
  }
}
function* getUserById(action: PayloadAction<IGetByIdRequest>) {
  try {
    const result: IApiResponse<IUser> = yield call(
      MasterDataApiService.getUserById,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getUserById.success(result.data!));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getUserById.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getUserById.failed(error));
  }
}
function* createUser(action: PayloadAction<ICreateUserRequest>) {
  try {
    const result: IApiResponse<IUser> = yield call(
      MasterDataApiService.createUser,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.createUser.success(result.data!));
      AlertHelper.showAlert("Tạo người dùng thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.createUser.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.createUser.failed(error));
  }
}

function* updateUser(action: PayloadAction<IUpdateUserRequest>) {
  try {
    const result: IApiResponse<IUser> = yield call(
      MasterDataApiService.updateUser,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.updateUser.success(result.data!));
      AlertHelper.showAlert(
        "Cập nhật người dùng thành công",
        EAlertType.SUCCESS
      );
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.updateUser.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.updateUser.failed(error));
  }
}

function* deleteUser(action: PayloadAction<{ id: string }>) {
  try {
    const result: IApiResponse<any> = yield call(
      MasterDataApiService.deleteUser,
      action.payload
    );
    if (result.succeeded) {
      yield put(MasterDataActions.deleteUser.success(result));
      AlertHelper.showAlert("Xoá người dùng thành công", EAlertType.SUCCESS);
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.deleteUser.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.deleteUser.failed(error));
  }
}

/**
 * @Common
 */

function* getProductCategories() {
  try {
    const result: IApiResponse<IProductCategory[]> = yield call(
      MasterDataApiService.getProductCategories
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getProductCategories.success(result.data!));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getProductCategories.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getProductCategories.failed(error));
  }
}

function* getUnitMeasure() {
  try {
    const result: IApiResponse<IUnitMeasure[]> = yield call(
      MasterDataApiService.getUnitMeasure
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getUnitMeasure.success(result.data!));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getUnitMeasure.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getUnitMeasure.failed(error));
  }
}
function* getZoneTemperature() {
  try {
    const result: IApiResponse<IZoneTemperature[]> = yield call(
      MasterDataApiService.getZoneTemperature
    );
    if (result.succeeded) {
      yield put(MasterDataActions.getZoneTemperature.success(result.data!));
    } else {
      const error = result as IErrorResponse;
      yield put(MasterDataActions.getZoneTemperature.failed(error));
    }
  } catch (error) {
    yield put(MasterDataActions.getZoneTemperature.failed(error));
  }
}

export function* Watcher() {
  /**
   * @Warehouse
   */
  yield takeEvery(MasterDataActions.getAllWarehouses.request, getAllWarehouses);
  yield takeEvery(MasterDataActions.getWarehouseById.request, getWarehouseById);
  yield takeEvery(MasterDataActions.createWarehouse.request, createWarehouse);
  yield takeEvery(MasterDataActions.updateWarehouse.request, updateWarehouse);
  yield takeEvery(MasterDataActions.deleteWarehouse.request, deleteWarehouse);

  /**
   * @Product
   */
  yield takeEvery(MasterDataActions.getAllProducts.request, getAllProducts);
  yield takeEvery(MasterDataActions.getProductById.request, getProductById);
  yield takeEvery(MasterDataActions.createProduct.request, createProduct);
  yield takeEvery(MasterDataActions.updateProduct.request, updateProduct);
  yield takeEvery(MasterDataActions.deleteProduct.request, deleteProduct);

  /**
   * @Pallet
   */
  yield takeEvery(MasterDataActions.getAllPallets.request, getAllPallets);
  yield takeEvery(MasterDataActions.createPallets.request, createPallets);

  /**
   * @Location
   */
  yield takeEvery(MasterDataActions.getAllLocations.request, getAllLocations);
  yield takeEvery(MasterDataActions.createLocation.request, createLocation);
  yield takeEvery(MasterDataActions.updateLocation.request, updateLocation);
  yield takeEvery(MasterDataActions.deleteLocation.request, deleteLocation);

  /**
   * @Zone
   */
  yield takeEvery(MasterDataActions.getAllZones.request, getAllZones);
  yield takeEvery(MasterDataActions.createZone.request, createZone);
  yield takeEvery(MasterDataActions.updateZone.request, updateZone);
  yield takeEvery(MasterDataActions.deleteZone.request, deleteZone);

  /**
   * @Company
   */
  yield takeEvery(MasterDataActions.getAllCompanies.request, getAllCompanies);
  yield takeEvery(MasterDataActions.getCompanyById.request, getCompanyById);
  yield takeEvery(MasterDataActions.createCompany.request, createCompany);
  yield takeEvery(MasterDataActions.updateCompany.request, updateCompany);
  yield takeEvery(MasterDataActions.deleteCompany.request, deleteCompany);

  /**
   * @User
   */
  yield takeEvery(MasterDataActions.getAllUsers.request, getAllUsers);
  yield takeEvery(MasterDataActions.getUserById.request, getUserById);
  yield takeEvery(MasterDataActions.createUser.request, createUser);
  yield takeEvery(MasterDataActions.updateUser.request, updateUser);
  yield takeEvery(MasterDataActions.deleteUser.request, deleteUser);

  /**
   * @Common
   */
  yield takeEvery(
    MasterDataActions.getProductCategories.request,
    getProductCategories
  );
  yield takeEvery(MasterDataActions.getUnitMeasure.request, getUnitMeasure);
  yield takeEvery(
    MasterDataActions.getZoneTemperature.request,
    getZoneTemperature
  );
}
